export const dictionary = {
  header: {
    switchToDarkMode: 'Dark mode',
    switchToLightMode: 'Light mode',
    objectives: 'Objectives',
    values: 'Values',
    skillTracker: 'Skill Tracker',
    stories: 'Stories',
    meetings: 'Meetings',
    programs: 'Programs',
  },

  auth: {
    login: {
      title: 'Login',

      buttonLogin: 'Sign In',
      buttonLoginWithGoogle: 'Login with Google',
      buttonLoginWithMicrosoft: 'Login with Microsoft',
      buttonCreateAccount: 'Create account',
      buttonResetPassword: 'Reset password',
      dontHaveAccount: 'Don\'t have an account?',
    },

    signUp: {
      title: 'Join StoryForge',

      buttonSignup: 'Create Account',
      buttonLogin: 'Sign In',
      alreadyHaveAccount: 'Already have an account?',
    },

    resetPassword: {
      title: 'Reset password',

      buttonLogin: 'Login',
      buttonReset: 'Reset password',

      passwordResetEmailSent: 'Password Reset Email Sent',
    },

    validations: {
      userWithThisEmailAlreadyExists: 'User with this email already exists',
      userNotFound: 'User not found',
      passwordsDoNotMatch: 'Passwords do not match',
      emailOrPasswordWrong: 'Email or password wrong',
      thePasswordMustBeAtLeast8CharactersLong: 'The password must be at least 8 characters long',
    },

    impersonate: {
      noToken: 'Token is not present',
      invalidToken: 'Token is invalid or expired',
    },

    firebase: {
      errors: {
        'auth/account-exists-with-different-credential': 'This account already exists with a different login provider',
      },
    },
    divider: 'or',
  },

  users: {
    labelTypeCustomer: 'Customer',
    labelTypeAdmin: 'Admin',
    labelTypeStaff: 'Staff',

    invite: {
      dialog: {
        title: 'Invite team members',
        placeholderEmail: 'Email, comma separated',
        buttonInvite: 'Send invite',
        invitationSentInfo: (email: string) => `Invitation to ${email} sent`,
      },

      title: 'Team invitation',
      subtitle: (company?: string) => company ? `You've been invited to join ${company}'s team` : 'You\'ve been invited to join a team',
      buttonAcceptInvitation: 'Create account',
      inviteButton: 'Invite Team Member',
    },

    team: {
      title: 'Team',

      detailsLabel: 'Details',

      buttonInvite: 'Invite user',
    },

    create: {
      title: 'Create user',
      buttonCreate: 'Create user',
    },

    edit: {
      buttonEdit: 'Edit user',
      buttonDelete: 'Delete user',
      deleteConfirmation: 'Are you sure you want to delete this user?',
    },
  },

  objectives: {
    title: 'Communication Objectives',
    subtitle: 'Define your communication objectives to enhance team collaboration',
    versionHistoryDescription: 'Track changes to your objectives over time',
    createdLabel: 'Objectives created',
  },

  values: {
    title: 'Leadership Values',
    subtitle: 'The core values you want your communication to transmit',
    versionHistoryDescription: 'Track changes to your values over time',
    createdLabel: 'Values created',
  },

  stories: {
    list: {
      title: 'Stories',
    },
    create: {
      title: 'Create story',
      buttonCreate: 'Create story',
    },
    edit: {
      buttonEdit: 'Edit story',
      buttonDelete: 'Delete story',
      deleteConfirmation: 'Are you sure you want to delete this story?',
    },
  },

  meetings: {
    list: {
      title: 'Meetings',
      tags: 'Tags',
      audience: 'Audience',
      desiredOutcome: 'Desired Outcome',
      nameLabel: 'Name',
      meetingTakeaway: 'Meeting Takeaway',
    },

    create: {
      title: 'Create meeting',
      subtitle: 'Plan your communication moment with structured preparation',
      buttonCreate: 'Create meeting',
    },

    edit: {
      title: 'Edit meeting',
      subtitle: 'Plan your communication moment with structured preparation',
      momentOfReflection: 'Moment of Reflection',
      meetingAdaptation: 'Meeting Adaptation',
      contextualAiFeedback: 'Contextual AI Feedback',
      buttonEdit: 'Edit meeting',
      buttonDelete: 'Delete meeting',
      deleteConfirmation: 'Are you sure you want to delete this meeting?',
      unlinkStoryConfirmation: 'Are you sure you want to unlink this story from the meeting?',
    },
  },

  companies: {
    list: {
      title: 'Companies',

      nameLabel: 'Name',
    },

    create: {
      title: 'Create company',
      buttonCreate: 'Create company',
    },

    edit: {
      buttonEdit: 'Edit company',
      buttonDelete: 'Delete company',
      deleteConfirmation: 'Are you sure you want to delete this company?',
    },
  },

  preferences: {
    versionSummary: {
      title: 'Version Summary',
      subtitle: 'Current version details and metadata',
      version: 'Version',
      lastUpdated: 'Last updated:',
      communicationScore: 'Communication Score',
      basedOnStoryAssessments: 'Based on story assessments',
      created: 'Created',
      emptyHistory: 'Version history is empty',
    },
    versionHistory: {
      title: 'Version History',
      noHistory: 'No Version History',
    },
  },

  dashboard: {
    steps: {
      defineGoals: {
        title: 'Define Your Goals',
        subtitle: 'Why are you telling stories?',
        description: 'Your stories gain power when tied to a clear communication outcome.',
        button: 'Define My Goals',
      },
      clarifyValues: {
        title: 'Clarify Your Values',
        subtitle: 'What do you stand for?',
        description: 'Stories shape perception. Values shape identity.',
        button: 'Set My Values',
      },
      buildStoryBank: {
        title: 'Build Your Story Bank',
        subtitle: 'Start Forging',
        description: 'Every leader has stories. Few leaders structure them.',
        button: 'Create a Story',
      },
    },
  },

  menu: {
    auth: {
      login: 'Log In',
      logout: 'Log Out',
      signUp: 'Signup',
    },
    dashboard: 'Dashboard',
    users: {
      list: 'Users',
      team: 'Team',
      myAccount: 'My account',
      create: 'Create user',
    },
    companies: {
      list: 'Companies',
      create: 'Create company',
    },
  },

  forms: {
    fieldEmail: 'Email',
    fieldPhone: 'Phone',
    fieldPassword: 'Password',
    labelOptional: '(Optional)',
    buttonCreate: 'Create',
    buttonSave: 'Save changes',
    buttonSaveShort: 'Save',
    buttonDelete: 'Delete',
    buttonEdit: 'Edit',
    buttonCancel: 'Cancel',
    fieldTagsPlaceholder: 'Add a tag ...',

    signup: {
      fieldRepeatPassword: 'Repeat Password',
    },
    user: {
      fieldName: 'Name',
      fieldLastName: 'Last name',
      fieldType: 'Type',
      fieldCompany: 'Company',
      fieldPosition: 'Position',
      fieldPicture: 'Upload picture',
      fieldSurname: 'Surname',
      fieldEmailDisabledHelper: 'Please contact support to change the email.',
    },
    company: {
      fieldName: 'Name',
      fieldPrimaryContact: 'Primary Contact',
    },

    onboarding: {
      story: {
        placeholder: 'Describe a specific leadership challenge you faced, focusing on your communication style, decision-making process, and how you influenced the outcome. Include details about stakeholder management, team dynamics, and lessons learned...',
      },
    },

    story: {
      placeholder: 'Write your full story here...',
      themePlaceholder: 'e.g., AI, Leadership, Innovation',
      coreMessagePlaceholder: 'Short statement of the takeaway',
      settingPlaceholder: 'Add setting detail',
      notesPlaceholder: 'Freeform reflections ...',
      titlePlaceholder: 'Enter story title',
      tagsPlaceholder: 'Add tags',
      tagsFilterPlaceholder: 'Filter by tags',
      storyTypePlaceholder: 'Select story type',
      allStoryTypes: 'All Types',
      linkedValuesPlaceholder: 'Select values',
      linkedObjectivesPlaceholder: 'Select objectives',

      storyTypeVision: 'Vision',
      storyTypeCulture: 'Culture',
      storyTypeAdversity: 'Adversity',
      storyTypeEnergyMomentum: 'Energy / Momentum',
      storyTypeOriginPurpose: 'Origin / Purpose',
      storyTypeTransformation: 'Transformation',
      storyTypeTeam: 'Team',
      storyTypeFailureLearning: 'Failure / Learning',
      storyTypeImpact: 'Impact',
      storyTypeLegacy: 'Legacy',

      status: {
        all: 'All Statuses',
        idea: 'Idea',
        draft: 'Draft',
        review: 'Review',
        staged: 'Staged',
        ready: 'Ready',
        archived: 'Archived',
      },
    },

    meeting: {
      fieldTitle: 'Title',
      fieldDate: 'Date',
      fieldAudience: 'Audience',
      fieldDesiredOutcome: 'Desired Outcome',
      fieldTags: 'Tags',
      fieldStatus: 'Status',
      allMeetingStatuses: 'All Statuses',

      fieldTitlePlaceholder: 'e.g. Q4 Board Meeting',
      fieldAudiencePlaceholder: 'e.g. Board of Directors',
      fieldDesiredOutcomePlaceholder: 'e.g. Secure Q1 budget approval',
      fieldMomentOfReflectionPlaceholder: 'Reflect on the meeting\'s impact and outcomes',

      meetingStatusPlaceholder: 'Select meeting status',

      meetingStatusPreparing: 'Preparing',
      meetingStatusReady: 'Ready',
      meetingStatusAwaitingReview: 'Awaiting Review',
      meetingStatusCompleted: 'Completed',

      createMeetingButton: 'Create Meeting',
    },

    validations: {
      required: 'This Field is Required',
      invalidDate: 'Date is Invalid',
      invalidEmail: 'Email is Invalid',
      invalidPhone: 'Phone number is Invalid',
      invalidYear: 'The year is incorrect',
      invalidPassword: 'Password is Invalid. At least 1 number is required.',
      invalidFileSize: (max: string) => `File is too big. Maximum is ${max}`,
      invalidFileType: 'File type is not valid',
      minLength: (length: number | string) => `Need to have at least ${length} characters`,
      maxLength: (length: number | string) => `Can't have more than ${length} characters`,
      oneOrMoreFieldsAreIncorrect: 'One or more fields are incorrect.',
      memberInvitationAllEmailsValid: 'One or more emails is invalid.',
    },
  },

  filters: {
    fieldSearch: 'Search',
  },

  feedback: {
    changesSaved: 'Changes saved successfully',
  },

  dialogs: {
    defaultTitle: 'Are you sure you want to do this?',
    defaultContent: 'This action is irreversible.',
    buttonNo: 'No',
    buttonYes: 'Yes',
    buttonCancel: 'Cancel',
    buttonProceed: 'Proceed',
    buttonClose: 'Close',
    unsavedChanges: {
      title: 'Unsaved changes',
      content: 'You have unsaved changes. Are you sure that you want to leave the page?',
    },
  },

  errors: {
    noPagePermission: 'You are not allowed to view this page',
    somethingWentWrong: 'Something went wrong!',
  },

  routes: {
    back: 'Back',
  },

  countries: {
    US: 'United States',
    AF: 'Afghanistan',
    AX: 'Aland Islands',
    AL: 'Albania',
    DZ: 'Algeria',
    AS: 'American Samoa',
    AD: 'Andorra',
    AO: 'Angola',
    AI: 'Anguilla',
    AQ: 'Antarctica',
    AG: 'Antigua And Barbuda',
    AR: 'Argentina',
    AM: 'Armenia',
    AW: 'Aruba',
    AU: 'Australia',
    AT: 'Austria',
    AZ: 'Azerbaijan',
    BS: 'Bahamas',
    BH: 'Bahrain',
    BD: 'Bangladesh',
    BB: 'Barbados',
    BY: 'Belarus',
    BE: 'Belgium',
    BZ: 'Belize',
    BJ: 'Benin',
    BM: 'Bermuda',
    BT: 'Bhutan',
    BO: 'Bolivia',
    BQ: 'Bonaire, Sint Eustatius and Saba',
    BA: 'Bosnia And Herzegovina',
    BW: 'Botswana',
    BV: 'Bouvet Island',
    BR: 'Brazil',
    IO: 'British Indian Ocean Territory',
    BN: 'Brunei Darussalam',
    BG: 'Bulgaria',
    BF: 'Burkina Faso',
    BI: 'Burundi',
    KH: 'Cambodia',
    CM: 'Cameroon',
    CA: 'Canada',
    CV: 'Cape Verde',
    KY: 'Cayman Islands',
    CF: 'Central African Republic',
    TD: 'Chad',
    CL: 'Chile',
    CN: 'China',
    CX: 'Christmas Island',
    CC: 'Cocos (Keeling) Islands',
    CO: 'Colombia',
    KM: 'Comoros',
    CG: 'Congo',
    CD: 'Congo, Democratic Republic of the',
    CK: 'Cook Islands',
    CR: 'CostaRica',
    CI: 'Cote d\'Ivoire',
    HR: 'Croatia',
    CU: 'Cuba',
    CW: 'Curacao',
    CY: 'Cyprus',
    CZ: 'Czech Republic',
    DK: 'Denmark',
    DJ: 'Djibouti',
    DM: 'Dominica',
    DO: 'Dominican Republic',
    EC: 'Ecuador',
    EG: 'Egypt',
    SV: 'El Salvador',
    GQ: 'Equatorial Guinea',
    ER: 'Eritrea',
    EE: 'Estonia',
    ET: 'Ethiopia',
    FK: 'Falkland Islands',
    FO: 'Faroe Islands',
    FJ: 'Fiji',
    FI: 'Finland',
    FR: 'France',
    GF: 'French Guiana',
    PF: 'French Polynesia',
    TF: 'French Southern Territories',
    GA: 'Gabon',
    GM: 'Gambia',
    GE: 'Georgia',
    DE: 'Germany',
    GH: 'Ghana',
    GI: 'Gibraltar',
    GR: 'Greece',
    GL: 'Greenland',
    GD: 'Grenada',
    GP: 'Guadeloupe',
    GU: 'Guam',
    GT: 'Guatemala',
    GG: 'Guernsey',
    GN: 'Guinea',
    GW: 'GuineaBissau',
    GY: 'Guyana',
    HT: 'Haiti',
    HM: 'Heard Island and McDonald Islands',
    VA: 'Holy See (Vatican City State)',
    HN: 'Honduras',
    HK: 'Hong Kong',
    HU: 'Hungary',
    IS: 'Iceland',
    IN: 'India',
    ID: 'Indonesia',
    IR: 'Iran',
    IQ: 'Iraq',
    IE: 'Ireland',
    IM: 'IsleOfMan',
    IL: 'Israel',
    IT: 'Italy',
    JM: 'Jamaica',
    JP: 'Japan',
    JE: 'Jersey',
    JO: 'Jordan',
    KZ: 'Kazakhstan',
    KE: 'Kenya',
    KI: 'Kiribati',
    KR: 'Korea, Republic of',
    KP: 'Korea, Democratic People\'s Republic of',
    XK: 'Kosovo',
    KW: 'Kuwait',
    KG: 'Kyrgyzstan',
    LA: 'Lao People\'s Democratic Republic',
    LV: 'Latvia',
    LB: 'Lebanon',
    LS: 'Lesotho',
    LR: 'Liberia',
    LY: 'Libya',
    LI: 'Liechtenstein',
    LT: 'Lithuania',
    LU: 'Luxembourg',
    MO: 'Macao',
    MK: 'Macedonia',
    MG: 'Madagascar',
    MW: 'Malawi',
    MY: 'Malaysia',
    MV: 'Maldives',
    ML: 'Mali',
    MT: 'Malta',
    MH: 'Marshall Islands',
    MQ: 'Martinique',
    MR: 'Mauritania',
    MU: 'Mauritius',
    YT: 'Mayotte',
    MX: 'Mexico',
    FM: 'Micronesia',
    MD: 'Moldova',
    MC: 'Monaco',
    MN: 'Mongolia',
    ME: 'Montenegro',
    MS: 'Montserrat',
    MA: 'Morocco',
    MZ: 'Mozambique',
    MM: 'Myanmar',
    NA: 'Namibia',
    NR: 'Nauru',
    NP: 'Nepal',
    NL: 'Netherlands',
    NC: 'NewCaledonia',
    NZ: 'NewZealand',
    NI: 'Nicaragua',
    NE: 'Niger',
    NG: 'Nigeria',
    NU: 'Niue',
    NF: 'Norfolk Island',
    MP: 'Northern Mariana  Islands',
    NO: 'Norway',
    OM: 'Oman',
    PK: 'Pakistan',
    PW: 'Palau',
    PS: 'Palestine, State of',
    PA: 'Panama',
    PG: 'Papua New Guinea',
    PY: 'Paraguay',
    PE: 'Peru',
    PH: 'Philippines',
    PN: 'Pitcairn',
    PL: 'Poland',
    PT: 'Portugal',
    PR: 'Puerto Rico',
    QA: 'Qatar',
    RE: 'Reunion',
    RO: 'Romania',
    RU: 'Russian Federation',
    RW: 'Rwanda',
    BL: 'Saint Barthelemy',
    SH: 'Saint Helena',
    KN: 'Saint Kitts And Nevis',
    LC: 'Saint Lucia',
    MF: 'Saint Martin',
    PM: 'Saint Pierre And Miquelon',
    VC: 'Saint Vincent and the Grenadines',
    WS: 'Samoa',
    SM: 'San Marino',
    ST: 'Sao Tome and Principe',
    SA: 'SaudiArabia',
    SN: 'Senegal',
    RS: 'Serbia',
    SC: 'Seychelles',
    SL: 'Sierra Leone',
    SG: 'Singapore',
    SX: 'Sint Maarten',
    SK: 'Slovakia',
    SI: 'Slovenia',
    SB: 'Solomon Islands',
    SO: 'Somalia',
    ZA: 'SouthAfrica',
    GS: 'South Georgia and the South Sandwich Islands',
    SS: 'South Sudan',
    ES: 'Spain',
    LK: 'Sri Lanka',
    SD: 'Sudan',
    SR: 'Suriname',
    SJ: 'Svalbard and Jan Mayen',
    SZ: 'Swaziland',
    SE: 'Sweden',
    CH: 'Switzerland',
    SY: 'Syrian Arab Republic',
    TW: 'Taiwan',
    TJ: 'Tajikistan',
    TZ: 'Tanzania',
    TH: 'Thailand',
    TL: 'TimorLeste',
    TG: 'Togo',
    TK: 'Tokelau',
    TO: 'Tonga',
    TT: 'Trinidad and Tobago',
    TN: 'Tunisia',
    TR: 'Turkey',
    TM: 'Turkmenistan',
    TC: 'Turks and Caicos Islands',
    TV: 'Tuvalu',
    UG: 'Uganda',
    UA: 'Ukraine',
    AE: 'United Arab Emirates',
    GB: 'United Kingdom',
    UM: 'United States Minor Outlying Islands',
    UY: 'Uruguay',
    UZ: 'Uzbekistan',
    VU: 'Vanuatu',
    VE: 'Venezuela',
    VN: 'Vietnam',
    VG: 'British Virgin Islands',
    VI: 'US Virgin Islands',
    WF: 'Wallis and Futuna',
    EH: 'Western Sahara',
    YE: 'Yemen',
    ZM: 'Zambia',
    ZW: 'Zimbabwe',
  },
};

export default dictionary;
